import React from 'react';
import { API } from '../api';
import { fmtTime12 } from '../utils/format';

/*
 * The club screen — what goes on the TV in the clubrooms.
 *
 * Nobody touches this. It is read from across a room by people holding a
 * drink, so everything is sized for five metres rather than fifty
 * centimetres, and the only motion is the rotation itself.
 *
 * Header and footer never move; the middle band changes. The pip for the
 * current slide drains rather than blinks, so a glance tells you how long
 * you have left to finish reading.
 *
 * A slide with nothing to say drops out of the rotation rather than
 * announcing "no matches today" to an empty room — see SLIDES[].available.
 * A club can ask for empty slides to be shown instead; that is the
 * `showEmpty` setting, driven from the laptop.
 */

export const SLIDE_IDS = ['draw', 'scores', 'ladder', 'comingUp', 'noticeboard', 'posters', 'photos'];

/* The two slides that are a picture and nothing else. They behave alike and
   differently from the rest: they never show empty, and several of them
   share their places in the rotation rather than getting one each. */
export const PICTURE_IDS = ['posters', 'photos'];

export const SLIDE_LABELS = {
  draw: "Today's draw",
  scores: 'Live scores',
  ladder: 'Club ladder',
  comingUp: 'Coming up',
  noticeboard: 'Noticeboard',
  posters: 'Posters',
  photos: 'Photos',
};

export const DEFAULT_SCREEN_SETTINGS = {
  seconds: 25,
  showEmpty: false,
  enabled: {
    draw: true, scores: true, ladder: true, comingUp: true, noticeboard: true,
    posters: true, photos: true,
  },
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Everything scheduled for today, played or not, across every competition. */
export function matchesToday(upcomingMatches, recentMatches) {
  const iso = todayISO();
  const seen = new Set();
  return [...(upcomingMatches || []), ...(recentMatches || [])]
    .filter(m => {
      if (seen.has(m.id)) return false;
      const onDate = m.date && String(m.date).slice(0, 10) === iso;
      const onSchedule = m.scheduledAt && String(m.scheduledAt).slice(0, 10) === iso;
      if (!onDate && !onSchedule) return false;
      seen.add(m.id);
      return true;
    })
    .sort((a, b) => (a.rink || 99) - (b.rink || 99));
}

export function openForEntry(comps) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return (comps || [])
    .filter(c => !c.registrationClosed && c.status !== 'in-progress' && c.status !== 'reopened' && c.status !== 'completed')
    .sort((a, b) => {
      /* Three tiers, same as the player home. A plain date sort puts a stale
         draft from a past season first, and on this screen "first" is what
         the QR in the footer points at — so it would send a whole clubroom
         to a competition that finished last year. */
      const tier = c => (!c.startDate ? 1 : (new Date(c.startDate) >= start ? 0 : 2));
      const ta = tier(a);
      const tb = tier(b);
      if (ta !== tb) return ta - tb;
      if (ta === 1) return 0;
      const d = new Date(a.startDate) - new Date(b.startDate);
      return ta === 0 ? d : -d;
    });
}

/** Which slides have something to show, given the data and the settings. */
/**
 * Everything on at the club, competitions and events together, soonest first.
 *
 * A member looking at the screen does not think "competitions" and "events";
 * they think "what is on". So the two are merged into one list and each row
 * says what kind of thing it is only where that is not obvious. Undated
 * things go last: they are real, but they are not what is next.
 */
export function comingUpItems(open, events) {
  const rows = [];
  (open || []).forEach(c => rows.push({
    id: 'c' + c.id,
    name: c.name,
    date: c.startDate ? String(c.startDate).slice(0, 10) : null,
    sub: Number(c.entryFee) > 0 ? '$' + Number(c.entryFee).toFixed(2) : 'Free to enter',
    isEvent: false,
  }));
  (events || []).filter(e => !e.cancelled).forEach(e => rows.push({
    id: 'e' + e.id,
    name: e.name,
    /* The night it is next on, which for a repeating event is not the night
       the series started. */
    date: (e.nextDate || e.startDate) ? String(e.nextDate || e.startDate).slice(0, 10) : null,
    /* An event's own words, in order of what a member most needs: when it
       starts, then what it costs, then where. */
    sub: [e.startTime, e.cost, e.location].map(x => (x || '').trim()).filter(Boolean).join(' · ')
      || (e.kind || 'Club event'),
    isEvent: true,
  }));
  /* The same three tiers openForEntry uses, and for the same reason. A plain
     ascending sort puts a stale competition from May above a market day next
     Saturday — and the first row here is what the screen announces as "Next
     at the club", so a whole clubroom gets told the wrong thing.

     Soonest still-to-come first; then undated, which are real but not next;
     then things already past, most recent first. */
  const todayStr = new Date().toISOString().slice(0, 10);
  const tier = r => (!r.date ? 1 : (r.date >= todayStr ? 0 : 2));
  return rows.sort((x, y) => {
    const tx = tier(x);
    const ty = tier(y);
    if (tx !== ty) return tx - ty;
    if (tx === 1) return 0;
    const d = x.date < y.date ? -1 : x.date > y.date ? 1 : 0;
    return tx === 0 ? d : -d;
  });
}

/**
 * The noticeboard: the events the club has nothing else to show for.
 *
 * An event with a live poster already has a slide of its own, and the poster
 * says it better than a row of text ever will — putting it here as well
 * would show a room the same market day twice in one lap. So the board is
 * what is left: the quiz night nobody made a picture for, written out
 * properly instead of squeezed into a list.
 *
 * The tick on the event is the club's say in it. On by default, because
 * publishing an event is already the deliberate act; the tick is how a
 * committee takes one back off the TV without unpublishing it.
 */
export function noticeboardItems(events, posters) {
  const postered = new Set(
    activePosters(posters).filter(p => p.EventId).map(p => String(p.EventId)),
  );
  return (events || [])
    .filter(e => !e.cancelled && e.showOnScreen !== false && !postered.has(String(e.id)))
    .map(e => ({
      id: e.id,
      name: e.name,
      date: (e.nextDate || e.startDate) ? String(e.nextDate || e.startDate).slice(0, 10) : null,
      repeat: e.repeat || null,
      time: (e.startTime || '').trim(),
      where: (e.location || '').trim(),
      cost: (e.cost || '').trim(),
      kind: (e.kind || '').trim(),
      details: (e.details || '').trim(),
    }))
    /* Soonest first, undated last: a date to be confirmed is real but it is
       not what a room needs to see at the top of the board. */
    .sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
    });
}

export function buildRotation({ comps, dashLeaderboard, events, photos, posters, recentMatches, settings, upcomingMatches }) {
  const today = matchesToday(upcomingMatches, recentMatches);
  const standings = ((dashLeaderboard && dashLeaderboard.standings) || []).filter(s => s.type === 'player');
  const open = openForEntry(comps);

  const available = {
    draw: today.length > 0,
    scores: today.length > 0,
    ladder: standings.length > 0,
    // An event is as much a reason to show this slide as a competition is.
    comingUp: open.length + comingUpItems([], events).length > 0,
    noticeboard: noticeboardItems(events, posters).length > 0,
    posters: activePosters(posters).length > 0,
    photos: screenPhotos(photos).length > 0,
  };

  const enabled = SLIDE_IDS.filter(id => {
    if (!settings.enabled[id]) return false;
    /* "Show it empty" means a heading over no rows, which still tells a room
       something — "no matches today". An empty picture slide is a blank
       rectangle, so those are always gated on having a picture. */
    if (PICTURE_IDS.includes(id)) return available[id];
    return settings.showEmpty || available[id];
  });

  const data = enabled.filter(id => !PICTURE_IDS.includes(id));
  const counts = {
    posters: activePosters(posters).length,
    photos: screenPhotos(photos).length,
  };
  const kinds = PICTURE_IDS.filter(id => enabled.includes(id));
  if (!kinds.length) return data;

  /* Pictures are spread through the rotation rather than bunched at the end,
     so a room never sits through every poster back to back and never waits a
     whole lap of everything else between two of them. */
  let wanted = kinds.map(id => ({ id, slots: posterSlots(data.length, counts[id]) }));

  /* Two kinds of picture asking for a slot each could between them crowd out
     the draw, which is the one thing on this screen somebody came to read.
     So the pair together keep to the same ceiling one of them had: never
     more places than there are other slides. The larger set gives up the
     extra places, since it is the one that queues fastest anyway. */
  const cap = Math.max(1, data.length);
  let total = wanted.reduce((n, k) => n + k.slots, 0);
  while (total > cap) {
    const biggest = wanted.reduce((a, b) => (b.slots > a.slots ? b : a));
    if (biggest.slots <= 1) break;
    biggest.slots -= 1;
    total -= 1;
  }
  wanted = wanted.filter(k => k.slots > 0);

  /* Alternating rather than all the posters and then all the photos: a lap
     that shows both is what having both on is for. */
  const queue = [];
  const left = wanted.map(k => k.slots);
  const totalSlots = left.reduce((n, x) => n + x, 0);
  let cursor = 0;
  while (queue.length < totalSlots) {
    const k = cursor % wanted.length;
    if (left[k] > 0) { queue.push(wanted[k].id); left[k] -= 1; }
    cursor += 1;
  }
  if (!queue.length) return data;
  if (!data.length) return queue;

  /* Spaced proportionally rather than every-nth. Every-nth leaves a
     remainder, and the remainder ends up dumped together at the end — four
     other slides and three posters gave "…poster, poster" back to back,
     which is the thing this was supposed to avoid. Placing by the running
     proportion never puts two together, because the queue is never longer
     than the number of other slides. */
  const out = [];
  let placed = 0;
  data.forEach((id, i) => {
    out.push(id);
    const want = Math.round(((i + 1) * queue.length) / data.length);
    while (placed < want && placed < queue.length) { out.push(queue[placed]); placed += 1; }
  });
  while (placed < queue.length) { out.push(queue[placed]); placed += 1; }
  return out;
}

/* Posters an admin has switched on, and whose event has not been and gone.
   A poster nobody took down after the event is worse than no poster. */
/**
 * How many places in the rotation the posters should take.
 *
 * One was too few. Five posters sharing a single slot means each is seen
 * once per lap — at 25s a slide and four other slides, that is one showing
 * every two minutes and ten minutes to get through them all. A club that
 * switched five posters on does not get five posters; it gets one, and a
 * suspicion the feature is broken.
 *
 * So they get a slot each, capped at the number of other slides, which keeps
 * posters to at most half the screen. Ten posters still cannot push the draw
 * to once every four minutes — they queue through the slots instead.
 */
export function posterSlots(dataCount, liveCount) {
  if (liveCount <= 0) return 0;
  return Math.min(liveCount, Math.max(1, dataCount));
}

/**
 * Which poster belongs in the slot showing now.
 *
 * Each slot advances a whole lap at a time, so with three slots and five
 * posters the screen shows 1,2,3 then 4,5,1 then 2,3,4 — every poster comes
 * round, and never the same one twice in a lap.
 */
export function posterIndexFor({ rotation, idx, tick, liveCount, kind = 'posters' }) {
  if (!rotation || !rotation.length || liveCount <= 0) return 0;
  const slots = rotation.filter(x => x === kind).length;
  if (!slots) return 0;
  const slot = rotation.slice(0, idx).filter(x => x === kind).length;
  const lap = Math.floor(tick / rotation.length);
  return (((lap * slots + slot) % liveCount) + liveCount) % liveCount;
}

/* The gallery pictures a club has switched on for the TV. Unlike a poster,
   a photograph has no event behind it and nothing to expire — a club takes
   one off the screen when it is tired of it. */
export function screenPhotos(photos) {
  return (photos || []).filter(p => p && p.imageData);
}

export function activePosters(posters) {
  const today = todayISO();
  return (posters || []).filter(p => p.active && (!p.showUntil || String(p.showUntil).slice(0, 10) >= today));
}

const dayLabel = d => new Date(d).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });

function ClubScreenView({ clubs, comps, dashLeaderboard, onExit, preview = false, recentMatches, selectedClubId, settings = DEFAULT_SCREEN_SETTINGS, upcomingMatches }) {
  const club = clubs.find(c => String(c.id) === String(selectedClubId)) || clubs[0];
  const [posters, setPosters] = React.useState([]);
  const [photos, setPhotos] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const rotation = buildRotation({ comps, dashLeaderboard, events, photos, posters, recentMatches, settings, upcomingMatches });

  /* Monotonic: it counts slides shown, not position in the rotation. The
     position is derived from it. Posters need to know how many times their
     slide has come round, and the position alone cannot say — it is the same
     number every time that slide is up. */
  const [tick, setTick] = React.useState(0);
  const idx = rotation.length ? tick % rotation.length : 0;
  const livePosters = activePosters(posters);
  const livePhotos = screenPhotos(photos);
  const [now, setNow] = React.useState(new Date());
  const [qr, setQr] = React.useState(null);

  const today = matchesToday(upcomingMatches, recentMatches);
  const standings = ((dashLeaderboard && dashLeaderboard.standings) || []).filter(s => s.type === 'player');
  const open = openForEntry(comps);
  const comingUp = comingUpItems(open, events);
  const notices = noticeboardItems(events, posters);

  /* How many separate things there are to look at. Not the same as the
     number of slides: several posters share one slide and take turns, so a
     club whose only slide is Posters still has something to advance to. That
     was the old test — rotation.length < 2 — and it left such a club frozen
     on its first poster for ever. */
  const viewCount = rotation.length
    + (rotation.includes('posters') ? Math.max(0, livePosters.length - 1) : 0)
    + (rotation.includes('photos') ? Math.max(0, livePhotos.length - 1) : 0);

  /* Restarted on every change of slide, so a slide stepped to by hand from a
     remote gets its full time rather than whatever was left on the last one. */
  React.useEffect(() => {
    if (preview || viewCount < 2) return undefined;
    const t = setInterval(() => setTick(i => i + 1), Math.max(5, settings.seconds) * 1000);
    return () => clearInterval(t);
  }, [preview, viewCount, settings.seconds, tick]);

  /* Full screen, with nothing on it to press. A TV has no use for an Exit
     button, and the faint one this used to have sat over the club's name.
     Instead the screen leaves on Escape, on OK/Enter from a remote, or on a
     click or tap anywhere; a remote's arrows step through the slides.
   *
     Escape in full screen is taken by the browser itself — it leaves full
     screen and the page never sees the key — so leaving full screen by any
     means also leaves the club screen. Only once it has actually been full
     screen: a TV browser that refuses full screen must not bounce straight
     back out. onExit is held in a ref because App passes a new function on
     every render, and re-running this would re-request full screen. */
  const exitRef = React.useRef(onExit);
  exitRef.current = onExit;
  const leave = React.useCallback(() => {
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    if (exitRef.current) exitRef.current();
  }, []);
  const live = !preview && !!onExit;
  React.useEffect(() => {
    if (!live) return undefined;
    const el = document.documentElement;
    if (!document.fullscreenElement && el.requestFullscreen) el.requestFullscreen().catch(() => {});
    let wasFull = !!document.fullscreenElement;
    const onFullscreen = () => {
      if (document.fullscreenElement) wasFull = true;
      else if (wasFull) leave();
    };
    const onKey = e => {
      if (['Escape', 'Enter', ' '].includes(e.key)) { e.preventDefault(); leave(); }
      else if (['ArrowRight', 'ArrowDown', 'PageDown'].includes(e.key)) { e.preventDefault(); setTick(i => i + 1); }
      else if (['ArrowLeft', 'ArrowUp', 'PageUp'].includes(e.key)) { e.preventDefault(); setTick(i => (i > 0 ? i - 1 : i)); }
    };
    document.addEventListener('fullscreenchange', onFullscreen);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreen);
      window.removeEventListener('keydown', onKey);
    };
  }, [live, leave]);

  // The clock in the header is the main reason this is worth looking at when
  // nothing else is happening, so it has to actually keep time.
  React.useEffect(() => {
    if (preview) return undefined;
    const t = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(t);
  }, [preview]);

  /* The footer QR points at the next competition open for entry, using the
     registration QR the app already generates. A generic "here is the app"
     code would be worse: this one enters you in something. */
  const qrCompId = open.length ? open[0].id : null;
  React.useEffect(() => {
    if (!qrCompId) { setQr(null); return; }
    let cancelled = false;
    fetch(`${API}/api/registrations/${qrCompId}/qr`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && d.qrCode) setQr(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [qrCompId]);
  /* Keyed on the id, not the club object: the object identity changes
     whenever the parent re-renders, and each poster is most of a megabyte —
     refetching them on every render would be expensive and invisible. */
  const posterClubId = club ? club.id : null;
  React.useEffect(() => {
    if (!posterClubId) { setPosters([]); return undefined; }
    let cancelled = false;
    fetch(`${API}/api/posters?clubId=${posterClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPosters(Array.isArray(d) ? d : []); })
      .catch(() => {});
    /* Only the gallery pictures switched on for the screen come back, at
       full size. Asking for the whole gallery would pull down every
       reference shot the club has ever uploaded to show three of them. */
    fetch(`${API}/api/gallery/screen?clubId=${posterClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setPhotos(Array.isArray(d) ? d : []); })
      .catch(() => {});
    /* Events without ?all, so the screen shows what a member should see:
       published, not cancelled, not already been and gone. */
    fetch(`${API}/api/events?clubId=${posterClubId}`)
      .then(r => (r.ok ? r.json() : []))
      .then(d => { if (!cancelled) setEvents(Array.isArray(d) ? d : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [posterClubId]);


  const current = rotation[idx] || null;
  /* Posters and photographs both take the whole frame, so both drop the
     header, the footer and the club colour behind them. */
  const showingPicture = PICTURE_IDS.includes(current);

  /* The frame is always 16:9. Everything inside is sized from its width, so
     without that the design distorts on any window that is not — on a laptop
     it is far too tall for the space and the ladder loses half its rows under
     a heading still claiming "top 8". A TV fills the frame exactly; a browser
     window gets bars in the club colour, which is what a scoreboard does. */
  return (
    <div className={`club-screen${preview ? ' club-screen-preview' : ''}${live ? ' club-screen-live' : ''}`}
      onClick={live ? leave : undefined}>
     {/* Said once, then gone: with no button on the screen, whoever put it
         up needs telling how to take it down. */}
     {live && <div className="club-screen-hint" aria-hidden="true">Press Esc or click to exit</div>}
     <div className={`club-screen-frame${showingPicture ? ' club-screen-frame-poster' : ''}`}>

      {/* A picture slide is the picture and nothing else. The header and
          footer are drawn only on the data slides: over a poster they
          covered the top and bottom tenth of a picture that was designed
          to be looked at, and no caption is worth a fifth of the thing it
          is captioning. The poster is 16:9 and so is the frame, so this is
          an exact fit — nothing cropped, nothing hidden. */}
      {current === 'posters' && (
        <SlidePosters
          posters={livePosters}
          turn={posterIndexFor({ rotation, idx, tick, liveCount: livePosters.length })} />
      )}

      {/* A photograph is not drawn to fit a 16:9 frame the way a poster is,
          so it is fitted inside rather than filling it — a phone photograph
          cropped to a letterbox loses the heads. */}
      {current === 'photos' && (
        <SlidePhotos
          photos={livePhotos}
          turn={posterIndexFor({ rotation, idx, tick, liveCount: livePhotos.length, kind: 'photos' })} />
      )}

      {!showingPicture && (
      <div className="club-screen-band">
        {club && club.logo
          ? <img src={club.logo} alt="" className="club-screen-logo" />
          : <span className="club-screen-logo club-screen-logo-blank" />}
        <span className="club-screen-club">{club ? club.name : 'GreenSheet'}</span>
        <span className="club-screen-clock">
          {now.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        </span>
        <span className="club-screen-clock club-screen-time">
          {now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
        </span>
      </div>
      )}

      <div className="club-screen-body">
        {!current && (
          <div className="club-screen-empty">
            <div className="club-screen-title">Nothing scheduled</div>
            <div className="club-screen-sub">
              {club ? club.name : ''} &middot; the screen will fill up when a competition is under way.
            </div>
          </div>
        )}

        {current === 'draw' && <SlideDraw matches={today} />}
        {current === 'scores' && <SlideScores matches={today} />}
        {current === 'ladder' && <SlideLadder standings={standings} />}
        {current === 'comingUp' && <SlideComingUp comingUp={comingUp} open={open} standings={standings} qr={qr} />}
        {current === 'noticeboard' && <SlideNoticeboard notices={notices} />}

      </div>

      {!showingPicture && (
      <div className="club-screen-foot">
        <div className="club-screen-pips">
          {rotation.map((id, i) => (
            <span key={`${id}-${i}`} className={`club-screen-pip${i === idx ? ' club-screen-pip-on' : ''}`}>
              {i === idx && !preview && (
                <span
                  className="club-screen-pip-fill"
                  // Restarting the animation needs a new element, not a new
                  // class — hence the index in the key.
                  key={`${id}-${idx}-${settings.seconds}`}
                  style={{ animationDuration: `${Math.max(5, settings.seconds)}s` }} />
              )}
              {i === idx && preview && <span className="club-screen-pip-fill club-screen-pip-static" />}
            </span>
          ))}
        </div>
        {/* Posters hold several places now; naming them once is what a
            reader wants, not "Posters · Posters · Posters". */}
        <div className="club-screen-foot-list">
          {rotation.filter((id, i) => rotation.indexOf(id) === i).map(id => SLIDE_LABELS[id]).join(' · ')}
        </div>
        <div className="club-screen-foot-right">
          {qr ? (
            <>
              <img src={qr.qrCode} alt="" className="club-screen-qr" />
              <span className="club-screen-foot-note">Scan to enter<br />{open[0].name}</span>
            </>
          ) : (
            <span className="club-screen-foot-note">{club ? club.name : ''}</span>
          )}
        </div>
      </div>
      )}

     </div>
    </div>
  );
}

function SlideDraw({ matches }) {
  const first = matches[0];
  return (
    <>
      <div className="club-screen-head">
        <div>
          <div className="club-screen-title">Today&rsquo;s draw</div>
          <div className="club-screen-sub">
            {first ? first.compName : ''}
            {first && first.round ? ` · Round ${first.round}` : ''}
          </div>
        </div>
        <div className="club-screen-count">{matches.length} match{matches.length === 1 ? '' : 'es'}</div>
      </div>
      <div className="club-screen-rows">
        <div className="club-screen-row club-screen-row-head">
          <span className="club-screen-rink">Rink</span>
          <span className="club-screen-side">Home</span>
          <span className="club-screen-v" />
          <span className="club-screen-side">Away</span>
          <span className="club-screen-when">Start</span>
        </div>
        {matches.slice(0, 7).map(m => (
          <div className="club-screen-row" key={m.id}>
            <span className="club-screen-rink club-screen-rink-num">{m.rink || '–'}</span>
            <span className="club-screen-side">{m.HomeTeam ? m.HomeTeam.name : 'TBC'}</span>
            <span className="club-screen-v">v</span>
            <span className="club-screen-side">{m.AwayTeam ? m.AwayTeam.name : 'Bye'}</span>
            <span className="club-screen-when">{m.time ? fmtTime12(m.time) : '–'}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function SlideScores({ matches }) {
  const done = matches.filter(m => m.completed).length;
  return (
    <>
      <div className="club-screen-head">
        <div>
          <div className="club-screen-title">Live scores</div>
          <div className="club-screen-sub">{matches[0] ? matches[0].compName : ''}</div>
        </div>
        <div className="club-screen-count">{done} of {matches.length} finished</div>
      </div>
      <div className="club-screen-cards">
        {matches.slice(0, 6).map(m => {
          const homeLeads = m.homeScore > m.awayScore;
          const awayLeads = m.awayScore > m.homeScore;
          const bye = !m.AwayTeam || !m.HomeTeam;
          return (
            <div className="club-screen-card" key={m.id}>
              <div className={`club-screen-cardlbl${m.completed ? ' club-screen-cardlbl-done' : ''}`}>
                Rink {m.rink || '–'} · {bye ? 'bye' : m.completed ? 'finished' : 'in play'}
              </div>
              {bye ? (
                <div className="club-screen-byename">{(m.HomeTeam || m.AwayTeam || {}).name || 'TBC'}</div>
              ) : (
                <div className="club-screen-cardbody">
                  <div className={`club-screen-sline${homeLeads ? ' club-screen-lead' : ''}`}>
                    <span className="club-screen-sname">{m.HomeTeam.name}</span>
                    <span className="club-screen-sval">{m.homeScore}</span>
                  </div>
                  <div className={`club-screen-sline${awayLeads ? ' club-screen-lead' : ''}`}>
                    <span className="club-screen-sname">{m.AwayTeam.name}</span>
                    <span className="club-screen-sval">{m.awayScore}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function SlideLadder({ standings }) {
  const top = standings.slice(0, 8);
  return (
    <>
      <div className="club-screen-head">
        <div>
          <div className="club-screen-title">Club ladder</div>
          <div className="club-screen-sub">This season · all competitions</div>
        </div>
        <div className="club-screen-count">Top {top.length} of {standings.length}</div>
      </div>
      <div className="club-screen-rows">
        <div className="club-screen-row club-screen-row-head">
          <span className="club-screen-pos">#</span>
          <span className="club-screen-who">Player</span>
          <span className="club-screen-col">Played</span>
          <span className="club-screen-col">Won</span>
          <span className="club-screen-col">Win %</span>
          <span className="club-screen-col">Rating</span>
        </div>
        {top.map((s, i) => (
          <div className={`club-screen-row club-screen-lrow${i < 3 ? ' club-screen-top3' : ''}`} key={s.id}>
            <span className="club-screen-pos">{i + 1}</span>
            <span className="club-screen-who">{s.name}</span>
            <span className="club-screen-col">{s.played}</span>
            <span className="club-screen-col">{s.wins}</span>
            <span className="club-screen-col">
              {s.played > 0 ? Math.round((s.wins / s.played) * 100) : 0}%
            </span>
            <span className="club-screen-col club-screen-colb">{s.rating}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* A poster is the whole slide. Generated at 16:9 for exactly this, so it
   fills the frame edge to edge with nothing stretched and nothing cropped;
   the header and footer float over it on a scrim.

   With several switched on they take turns rather than each claiming a place
   in the rotation, so ten posters do not push the draw to once every four
   minutes. */
function SlidePosters({ posters, turn }) {
  if (!posters.length) return null;
  const poster = posters[turn % posters.length];
  return (
    <div className="club-screen-poster-wrap">
      <img src={poster.imageData} alt={poster.title} className="club-screen-poster" />
    </div>
  );
}

/**
 * A photograph from the club's gallery, filling the frame.
 *
 * Unlike a poster, a photograph was not drawn to fit this shape — it is
 * whatever came off somebody's phone. So it is fitted inside the frame
 * rather than cropped to it, on a dark ground, with the club's own caption
 * along the bottom. The caption is the label from the gallery, which is what
 * an admin already typed to find the picture again: "prize giving 2025".
 */
function SlidePhotos({ photos, turn }) {
  if (!photos.length) return null;
  const photo = photos[turn % photos.length];
  return (
    <div className="club-screen-photo-wrap">
      <img src={photo.imageData} alt={photo.label || ''} className="club-screen-photo" />
      {photo.label && <div className="club-screen-photo-cap">{photo.label}</div>}
    </div>
  );
}

const REPEAT_WORDS = {
  weekly: 'Every week',
  fortnightly: 'Every second week',
  monthly: 'Every month',
  'monthly-nth': 'Every month',
};

/**
 * The noticeboard: what is pinned up, as cards rather than a list.
 *
 * A list of names and dates is what the Coming up slide already is. This
 * slide exists for the events with no poster, so it has to do the poster's
 * job — say what the thing is, when it is, what it costs and what a member
 * should know — at a size that reads from across the room.
 *
 * Six at most, and the details trimmed: a card nobody can read from five
 * metres is not on a noticeboard, it is on a page.
 */
function SlideNoticeboard({ notices = [] }) {
  const shown = notices.slice(0, 6);
  return (
    <>
      <div className="club-screen-head">
        <div>
          <div className="club-screen-title">Noticeboard</div>
          <div className="club-screen-sub">What&rsquo;s on at the club</div>
        </div>
        <div className="club-screen-count">
          {notices.length} notice{notices.length === 1 ? '' : 's'}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="club-screen-empty">
          <div className="club-screen-sub club-screen-sub-lg">Nothing on the board just now.</div>
        </div>
      ) : (
        <div className={`club-screen-notices${shown.length > 3 ? ' club-screen-notices-dense' : ''}`}>
          {shown.map(n => (
            <div className="club-screen-notice" key={n.id}>
              <div className="club-screen-notice-when">
                {n.date ? dayLabel(n.date) : 'Date to be confirmed'}
                {n.repeat && <span className="club-screen-notice-rep">{REPEAT_WORDS[n.repeat] || 'Repeats'}</span>}
              </div>
              <div className="club-screen-notice-name">{n.name}</div>
              <div className="club-screen-notice-sub">
                {[n.time, n.where, n.cost].filter(Boolean).join(' · ') || n.kind}
              </div>
              {/* Trimmed rather than wrapped to four lines: at this size the
                  fourth line pushes the next card off the screen. */}
              {n.details && (
                <div className="club-screen-notice-detail">
                  {n.details.length > 150 ? `${n.details.slice(0, 150).trimEnd()}…` : n.details}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SlideComingUp({ comingUp = [], open, qr, standings }) {
  /* What is next is whatever is next, competition or event. A market day on
     Saturday outranks a championship in November on a screen whose whole job
     is telling a room what is happening soon. */
  const next = comingUp[0] || open[0];
  const top3 = standings.slice(0, 3);
  return (
    <div className="club-screen-split">
      <div className="club-screen-split-main">
        <div className="club-screen-label">Next at the club</div>
        {next && (
          <>
            <div className="club-screen-huge">{next.name}</div>
            <div className="club-screen-sub club-screen-sub-lg">
              {next.date || next.startDate ? dayLabel(next.date || next.startDate) : 'Date to be confirmed'}
              {next.sub !== undefined
                ? (next.sub ? ` · ${next.sub}` : '')
                : (Number(next.entryFee) > 0 ? ` · $${Number(next.entryFee).toFixed(2)}` : ' · Free to enter')}
            </div>
          </>
        )}
        {top3.length > 0 && (
          <div className="club-screen-leaders">
            <div className="club-screen-label">Leading the ladder</div>
            <div className="club-screen-leaders-row">
              {top3.map((s, i) => (
                <div key={s.id}>
                  <div className="club-screen-leader"><span>{i + 1}</span>&nbsp;&nbsp;{s.name}</div>
                  <div className="club-screen-sub">{s.rating}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="club-screen-split-side">
        <div className="club-screen-label">{comingUp.length ? 'Also on' : 'Open for entry'}</div>
        <div className="club-screen-erows">
          {(comingUp.length ? comingUp.slice(1, 5) : open.slice(0, 4)).map(c => (
            <div className="club-screen-erow" key={c.id}>
              <span className="club-screen-ename">{c.name}</span>
              <span className="club-screen-edate">
                {(c.date || c.startDate) ? dayLabel(c.date || c.startDate) : '—'}
              </span>
            </div>
          ))}
        </div>
        {qr && (
          <div className="club-screen-qrcard">
            <img src={qr.qrCode} alt="" className="club-screen-qrbig" />
            <div>
              <div className="club-screen-qrtitle">Enter from your phone</div>
              <div className="club-screen-sub">Point your camera at the code.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClubScreenView;
